import { datadogRum } from "@datadog/browser-rum";
import { debug as debugFn } from "debug";

import { isBrowserExtensionError } from "@thalamos/frontend";
import type { CurrentUser } from "@thalamos/haven-common";

import { config } from "./config/index.js";
import { keepRumEventPrivate } from "./rumPrivacy.js";

const debug = debugFn("haven-frontend:tracing");

export function initialiseTracing() {
  if (config.datadog.disableTracing) {
    return;
  }

  const ownOrigins = [
    window.location.origin,
    new URL(config.backendUrl).origin,
  ];

  // The SDK's defaults are not stable across majors, so every option whose
  // value we depend on is set explicitly rather than inherited. Some already
  // match today's default and are pinned anyway, so a future flip cannot
  // change our behaviour silently.
  datadogRum.init({
    applicationId: config.datadog.applicationId,
    clientToken: config.datadog.clientToken,
    site: "uk1.datadoghq.com",
    service: "haven-frontend",
    env: config.environment,
    version: config.version,
    sessionSampleRate: 100,
    // Replay would record an assessment about a child, so it stays off until
    // enabling it is a deliberate decision.
    sessionReplaySampleRate: 0,
    trackUserInteractions: false,
    allowedTracingUrls: [config.backendUrl],

    // Enabled, this adds a `baggage` request header that the server's CORS
    // allowHeaders do not list, so the call fails preflight — costing the
    // request itself, not just its trace. The shipped typings document a
    // `false` default that the runtime does not honour.
    propagateTraceBaggage: false,

    // Resource URLs are captured, so a path carrying anything identifying about
    // a child would land in Datadog: keep paths to ids. Query strings are
    // stripped on the way out — see `keepRumEventPrivate`.
    trackResources: true,
    trackLongTasks: true,

    // Deliberately stricter than the SDK default, which masks only user input.
    defaultPrivacyLevel: "mask",
    // Should click tracking be turned on, an action is named from what was
    // clicked — and a row in the young people's directory is a child's name.
    enablePrivacyForActionName: true,

    // An error thrown entirely by a browser extension is not ours to fix. Any
    // other event leaves only once it is fit to — see `keepRumEventPrivate`.
    beforeSend: (event) =>
      !isBrowserExtensionError(event) && keepRumEventPrivate(event, ownOrigins),
  });
}

export function setUser(user: CurrentUser | null) {
  if (config.datadog.disableTracing) {
    debug("tracing disabled; would set user to", user?.id);
    return;
  }

  // By id and session context, under the keys the server's `actorFields` uses,
  // so a session joins its requests' lines — keep the two in step. Not by name
  // or address, which RUM would attach to every event the session sends.
  if (user) {
    datadogRum.setUser({
      id: user.id,
      ...(user.sessionContext === null
        ? {}
        : {
            organisationId: user.sessionContext.organisationId,
            sessionContextKind: user.sessionContext.kind,
            roles: user.sessionContext.roles.join(","),
          }),
    });
  } else {
    datadogRum.clearUser();
  }
}

export function logErrorToDatadog(error: Error) {
  debug(error);
  if (!config.datadog.disableTracing) {
    datadogRum.addError(error);
  }
}
